// src/api/jobHolder/jobHolderApi.js
import { apiRequest, getRequest, ApiError } from "../apiConfig";

/** ✅ unwrap list from different backend shapes */
function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.employees)) return payload.employees;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

/** ✅ safe fallback only for 404/405 */
async function callWithMethodFallback(endpoint, data, methods = ["PUT", "PATCH", "POST"]) {
  let lastErr = null;
  for (const method of methods) {
    try {
      return await apiRequest(endpoint, { method, data });
    } catch (e) {
      lastErr = e;
      const status = e instanceof ApiError ? e.status : e?.status;
      if (status !== 404 && status !== 405) throw e;
    }
  }
  throw lastErr;
}

/** ✅ map employee data safely (prevents undefined UI crashes) */
function mapEmployee(e) {
  const id = e?._id || e?.id;

  const fullName =
    e?.fullName ||
    e?.full_name ||
    [e?.firstName || e?.first_name, e?.lastName || e?.last_name].filter(Boolean).join(" ") ||
    e?.name ||
    "—";

  return {
    _id: id,
    id,

    fullName,
    starRecId:
      e?.starRecId ||
      e?.starrec_id ||
      e?.star_rec_id ||
      e?.employeeId ||
      e?.employee_id ||
      "—",

    taxId: e?.taxId || e?.tax_id || e?.pan || e?.aadhar || "—",
    businessName:
      e?.businessName ||
      e?.business_name ||
      e?.currentBusinessName ||
      e?.current_business_name ||
      "—",

    country: e?.country || "",
    state: e?.state || "",
    city: e?.city || "",

    position: e?.position || e?.role || "",
    dateOfJoining: e?.dateOfJoining || e?.date_of_joining || e?.doj || "",
    ctc: e?.ctc ?? e?.current_ctc ?? "",

    updatedCtcDate: e?.updatedCtcDate || e?.updated_ctc_date || "",
    updatedCtcCtc: e?.updatedCtcCtc ?? e?.updated_ctc ?? "",

    dateOfExit: e?.dateOfExit || e?.date_of_exit || "",
    didntExit: !!(e?.didntExit ?? e?.did_not_exit ?? false),

    personalEmail: e?.personalEmail || e?.personal_email || e?.email || "",
    mobileNumber: e?.mobileNumber || e?.mobile_number || e?.mobile || "",

    isBlacklisted: !!(e?.blacklisted ?? e?.isBlacklisted ?? false),
    blacklisted: !!(e?.blacklisted ?? e?.isBlacklisted ?? false),
    blacklistReason: e?.blacklistReason || e?.blacklist_reason || e?.reason || "",
  };
}

/** ✅ GET list */
export async function getEmployees(filters = {}) {
  const params = {};

  if (filters.country) params.country = filters.country;
  if (filters.state) params.state = filters.state;
  if (filters.city) params.city = filters.city;

  // backend expects true/false
  if (filters.blacklisted === true) params.blacklisted = "true";
  if (filters.blacklisted === false) params.blacklisted = "false";

  const payload = await getRequest("/admin/employees", params);
  return unwrapList(payload).map(mapEmployee);
}

/** ✅ GET details by id */
export async function getEmployeeById(id) {
  const payload = await getRequest(`/admin/employees/${id}`);
  return mapEmployee(payload?.data || payload);
}

/** ✅ blacklist */
export async function blacklistEmployee(id, reason = "") {
  return await callWithMethodFallback(`/admin/employees/${id}/blacklist`, { reason });
}

/** ✅ unblacklist */
export async function unblacklistEmployee(id) {
  // ✅ most backends use POST/PUT for "unblacklist"
  try {
    return await apiRequest(`/admin/employees/${id}/unblacklist`, {
      method: "POST",
      data: {}, // keep body for strict servers
    });
  } catch (e) {
    const status = e instanceof ApiError ? e.status : e?.status;

    // ✅ If POST not allowed, try other methods
    if (status === 404 || status === 405) {
      return await callWithMethodFallback(
        `/admin/employees/${id}/unblacklist`,
        {},
        ["PUT", "PATCH", "DELETE"] // DELETE last
      );
    }
    throw e;
  }
}
